/**
 * ViewPerCurrency Component
 *
 * This component renders a table footer with data specific to a given column.
 * It displays either a sum of currency data or a button to close the preview,
 * depending on the type of column.
 *
 * The component calculates and updates the dialog height based on:
 *  - number of visible records
 *  - current row density
 *
 * To avoid infinite render loops:
 *  - `useEffect` is used instead of `useLayoutEffect`
 *  - Redux dispatch is guarded to run only when values actually change
 *
 * Props:
 *  - `column` (string): The column identifier.
 *  - `isLastColumn` (boolean): Indicates if this is the last visible table column.
 *  - `className` (string): CSS class controlling footer visibility and styling.
 *
 * State / Refs:
 *  - `viewPerCcyRef`: Reference to the footer wrapper element.
 *  - `prevDispatchRef`: Stores the last dispatched height values to prevent
 *    redundant Redux updates.
 *
 * External Data:
 *  - Currency data is retrieved via `useViewPerCurrency`
 *  - Row density is derived from `useRowDensity`
 *  - Visible columns are derived from Redux column order
 *
 * Conditional Rendering:
 *  - Geo columns (excluding the last before CCY) are rendered as empty footers
 *  - The last column before CCY renders a "Close Preview" button
 *  - The CCY column renders summed currency values
 */

import React, { useEffect, useRef } from 'react';
import { t } from 'i18next';
import { StatsTableColumn } from '../../../../../enum/TableColumn';
import {
  borderStyle,
  getLastColumnBeforeCurrency,
  getTableClass,
  isGeoColumnExcludingLastBeforeCCY,
} from '../../../../../util/table-helper';
import { useViewPerCurrency } from '../../../../../hook/useViewPerCurrency';
import { useRowDensity } from '../../../../../hook/useRowDensity';
import { setShouldViewPerCurrency, setViewPerCcyDialogHeight } from '../../../../../store/slice/stats-slice';
import store from '../../../../../store/store';
import Button from '../../Button';
import { getCurrencyIcon } from '../../../../../util/currency';
import FooterWrapper from './FooterWrapper';
import { useAppSelector } from '../../../../../hook/useAppSelector';
import { usePageVisibleColumns } from '../../../../../hook/usePageVisibleColumns';
import { formatColumnValue } from '../../../../../util/formatColumnValue';

interface TableFooterProps {
  column: string;
  isLastColumn: boolean;
  className: string;
  visibleFooter: boolean;
}

const ViewPerCurrency: React.FC<TableFooterProps> = ({ column, isLastColumn, className, visibleFooter }) => {
  const format = useAppSelector((state) => state.configSlice.format);

  /**
   * Retrieve aggregated data for the given column.
   * For CCY columns this represents currency sums.
   */
  const data = useViewPerCurrency(column);

  /**
   * Retrieve visible columns from Redux and filter out hidden ones.
   * Memoized to avoid unnecessary recalculations.
   */
  const { visibleColumns } = usePageVisibleColumns();

  /**
   * Row density settings control visual spacing and row height.
   */
  const { rowDensityClass, rowDensityHeight } = useRowDensity();

  /**
   * Reference to the footer container used for height-related calculations.
   */
  const viewPerCcyRef = useRef<HTMLDivElement>(null);

  /**
   * Determine column position relative to the CCY column.
   */
  const lastBeforeCurrency = getLastColumnBeforeCurrency(
    visibleColumns.map((c) => c.name),
    (col) => col === StatsTableColumn.CCY,
  );

  const isLastBeforeCurrency = column === lastBeforeCurrency;
  const isCCYColumn = column === StatsTableColumn.CCY;

  /**
   * Heights derived from Redux store for geo and location columns.
   */
  const geoColumnHeight = store.getState().stats.geoColumnHeight ?? 0;
  const locationColumnHeight = store.getState().stats.locationColumnHeight ?? 0;

  /**
   * Closes the "View Per Currency" preview dialog.
   */
  const handleClosePreview = () => store.dispatch(setShouldViewPerCurrency());
  const isMobile = useAppSelector((state) => state.configSlice.isMobile);

  /**
   * Stores the previously dispatched dialog height values.
   * Used to prevent infinite render → dispatch loops.
   */
  const prevDispatchRef = useRef<{
    rows: number;
    height: number;
  } | null>(null);

  /**
   * Update the View Per Currency dialog height when:
   *  - number of records changes
   *  - row density changes
   *
   * Guarded to prevent redundant Redux updates.
   */
  useEffect(() => {
    if (!viewPerCcyRef.current || data.length === 0) return;

    const next = {
      rows: data.length,
      height: rowDensityHeight,
    };

    // Prevent dispatch if values have not changed
    if (prevDispatchRef.current?.rows === next.rows && prevDispatchRef.current?.height === next.height) {
      return;
    }

    prevDispatchRef.current = next;

    store.dispatch(
      setViewPerCcyDialogHeight({
        numberOfRecords: next.rows,
        rowDensityHeight: next.height,
      }),
    );
  }, [data.length, rowDensityHeight]);

  /**
   * Skip rendering for geo columns excluding the last column before CCY.
   */
  if (isGeoColumnExcludingLastBeforeCCY(column, visibleColumns)) {
    return (
      <FooterWrapper column={column} className={className} height={className === 'hidden_footer' ? 0 : geoColumnHeight}>
        {null}
      </FooterWrapper>
    );
  }

  return (
    <FooterWrapper
      column={column}
      className={className}
      isCCYColumn={isCCYColumn}
      isLastColumnBeforeCurrency={isLastBeforeCurrency}
      refProp={viewPerCcyRef}
    >
      {/* Render footer rows for all columns except the last before CCY */}
      {!isLastBeforeCurrency &&
        data.map((cellData, idx) => {
          const displayValue = formatColumnValue(column, cellData, format);
          return (
            <div
              key={idx}
              className={`table_row ${getTableClass(column, 'footer_row', visibleColumns, isMobile)}`}
              style={borderStyle({
                idx,
                dataLength: data.length,
                isLastColumn,
                isCCYColumn,
                visibleFooter: true,
              })}
            >
              <div
                className={`table_cell ${rowDensityClass} ${isCCYColumn ? 'sum_ccy_details' : ''}`}
                style={{
                  justifyContent: !isCCYColumn ? 'end' : 'start',
                }}
              >
                {isCCYColumn && 'Σ'}
                {isCCYColumn && (
                  <span className="ccy_details">
                    <img className="ccy_flag" src={getCurrencyIcon(cellData)} alt={`ccy_${cellData}`} />
                    <span>
                      <p>{t(`${cellData}`)}</p>
                    </span>
                  </span>
                )}{' '}
                {displayValue}
              </div>
            </div>
          );
        })}

      {/* Render close preview button only for the last column before CCY */}
      {isLastBeforeCurrency && (
        <div
          className={`table_row ${getTableClass(column, 'footer_row', visibleColumns, isMobile)} last_geo_column`}
          style={{
            paddingTop: `${className === 'hidden_footer' ? 0 : locationColumnHeight}px`,
          }}
        >
          <Button className={`button view_per_currency ${rowDensityClass}`} onClick={handleClosePreview}>
            {t('statisticPage.table.buttons.closePreview')}
          </Button>
        </div>
      )}
    </FooterWrapper>
  );
};

export default ViewPerCurrency;
